import React, {
  ReactNode,
  useContext,
  useEffect,
  useReducer,
  useRef,
} from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useIsFetching } from "react-query";
import { Outlet } from "react-router";

import { Box } from "@mui/system";

import { Guide } from "../guides/components/Guide";
import { useGuide } from "../guides/context";
import { ErrorBoundaryFallback } from "./ErrorBoundaryFallback";
import { LazyLoadingFallback } from "./LazyLoadingFallback";
import { ScheduledAlerts } from "./ScheduledAlerts";
import { SideNav } from "./SideNav";
import TopBar from "./TopBar";

export default Shell;

export function Shell() {
  const isFetching = useIsFetching();
  const [state, dispatch] = useShellReducer();
  const { state: shellState } = useGuide() ?? {};

  return (
    <ShellContextProvider state={state} dispatch={dispatch}>
      <Box
        sx={{
          display: "grid",
          height: "100vh",
          gridTemplateAreas: `
          "alert alert  alert"
          "left  topbar guide"
          "left  body   guide"
        `,
          gridTemplateColumns: "auto 1fr auto",
          gridTemplateRows: "auto auto 1fr",
        }}
      >
        <Box
          ref={state.modalContainer}
          sx={{
            position: "relative",
            gridRowStart: "topbar",
            gridRowEnd: "body",
            gridColumnStart: "body",
            gridColumnEnd: "body",
            overflow: "hidden",
            pointerEvents: "none",
          }}
        />
        <Box sx={{ gridArea: "alert" }}>
          <ScheduledAlerts />
        </Box>
        <Box sx={{ gridArea: "topbar" }}>
          <TopBar
            disableLogo
            isLoading={isFetching > 0}
            renderNav={state.TopNav ? () => <state.TopNav /> : null}
          />
        </Box>
        <SideNav sx={{ gridArea: "left", placeSelf: "stretch" }} />
        <Box
          sx={{
            gridArea: "body",
            overflow: "auto",
          }}
        >
          <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
            <React.Suspense fallback={<LazyLoadingFallback />}>
              <Outlet />
            </React.Suspense>
          </ErrorBoundary>
        </Box>
        {shellState?.activeGuide && (
          <Box
            sx={{
              gridArea: "guide",
              position: "sticky",
              top: 0,
              overflowY: "auto",
              overflowX: "hidden",
              bgcolor: "background.paper",
              borderLeft: "solid 1px rgb(90% 90% 90%)",
            }}
          >
            <Guide
              useCase={shellState?.activeGuide}
              sx={{ width: "clamp(58ch, 28vw, 120ch)" }}
            />
          </Box>
        )}
      </Box>
    </ShellContextProvider>
  );
}

export const ShellContext = React.createContext<{
  state: ShellState;
  dispatch: (action: ShellAction) => void;
}>({
  state: {
    modalContainer: { current: document?.body },
  },
  dispatch() {
    console.warn("No provider setup for ShellContext");
  },
});

function ShellContextProvider(props: {
  state: ShellState;
  dispatch: (a: ShellAction) => void;
  children: ReactNode;
}) {
  return (
    <ShellContext.Provider
      value={{ state: props.state, dispatch: props.dispatch }}
    >
      {props.children}
    </ShellContext.Provider>
  );
}

export type ShellState = {
  modalContainer: React.MutableRefObject<HTMLElement>;
  TopNav?: React.FC<{}>;
};

export type ShellAction =
  | { type: "set_top_nav"; component: React.FC<{}> }
  | { type: "clear_top_nav" };

function useShellReducer() {
  const modalContainer = useRef<HTMLElement>(null);

  return useReducer(
    (state: ShellState, action: ShellAction) => {
      const type = action.type;
      switch (type) {
        case "set_top_nav":
          if (state.TopNav) {
            console.warn(
              // In practice, one simply clobbers the other.
              "Multiple top nav elements set. This is unsupported, and the behavior is undefined."
            );
          }
          return {
            ...state,
            TopNav: action.component,
          };
        case "clear_top_nav":
          return {
            ...state,
            TopNav: null,
          };
        default:
          let unhandled: never = type;
          throw new Error(`Unhandled action: ${unhandled}`);
      }
    },
    { TopNav: null, modalContainer }
  );
}

export function useShell() {
  return useContext(ShellContext);
}

export function TopNav({ component }: { component: React.FC<{}> }) {
  const shell = useShell();

  useEffect(() => {
    shell.dispatch({ type: "set_top_nav", component });
    return () => shell.dispatch({ type: "clear_top_nav" });
  }, [component]);

  return null;
}
